
import React, { useState } from 'react'

import helper from '@utils/helper';
import I18n from '@utils/i18n'

import { MaxBuyAvblMessage } from '../BaseForm'
import { useBaseForm } from './useBaseForm'
import { useBuyForm } from './useBuyForm'
import { useValidata } from './useValidata';

export const useMarketBuy = (params) => {
  const { coinDetail, assetCoinDetail, marginBalance, fetchMarginBalance, isLogin, close, tradeStore } = params;
  const [currentInputFocus, setCurrentInputFocus] = useState(null);
  const { state: validState, updateValidState } = useValidata()
  const {
    form,
    updateBaseQty,
    updateOrderTotal,
    updateSlider,
    publicMarginSelected,
    loading,
    createTradeOrder,
    jumpLogin,
    handleMarginDisabled
  } = useBaseForm({
    coinDetail, tradeStore, currentInputFocus, close, marginBalance, priceKey: 'ask', isMarket: true, fetchMarginBalance
  });
  const {
    renderMaxAvbl,
    renderSpotAvbl,
    renderEstimateBorrow,
    maxAvblAndSpotAvblRef
  } = useBuyForm({ marginBalance, form, coinDetail, assetCoinDetail })

  // 买入卖出
  const onBaseQtyChange = (e) => {
    const price = coinDetail?.ask
    updateBaseQty({ value: e?.target?.value, price })
    if (isLogin) {
      checkTotalIsValid()
      checkBaseQtyValid()
      updateProgress()
    }
  }

  // 成交额
  const onOrderTotalChange = (e) => {
    const price = coinDetail?.ask
    updateOrderTotal({ value: e?.target?.value, price })
    if (isLogin) {
      checkTotalIsValid()
      checkBaseQtyValid()
      updateProgress()
    }
  }

  // 更改滑杆进度
  const updateProgress = () => {
    const price = coinDetail?.ask;
    let maxBuyAvail = marginBalance?.()?.additional + Math.abs(marginBalance?.()?.unwindBuyQty * price);
    const marginSelected = form.getFieldValue('marginSelected'); // 是否使用杠杆
    const orderTotal = form.getFieldValue('orderTotal'); // 成交额
    if (!marginSelected) {
      maxBuyAvail = maxBuyAvail < marginBalance?.()?.quoteMarketAvailable ? maxBuyAvail : marginBalance?.()?.quoteMarketAvailable
      if (['USDT_USD', 'USDT_HKD'].includes(coinDetail?.coinCode)) {
        maxBuyAvail = Math.max(maxBuyAvail, marginBalance?.()?.quoteMarketAvailable)
      }
    }
    const radio = maxBuyAvail > 0 ? (parseFloat(orderTotal || '0') / maxBuyAvail) : 0;
    const progress = radio > 0 ? (radio >= 1 ? 100 : Math.ceil(radio * 100)) : 0;
    form.setFieldsValue({ progress })
  }

  // 用户拉动滑杆
  const onSlider = (value) => {
    if (!isLogin) {
      jumpLogin()
      return
    }
    setCurrentInputFocus('orderTotal')
    const price = coinDetail?.ask;
    const spotAvblBuyAmount = Number(marginBalance?.()?.quoteAvailable / price)
    const marginAvblBuyAmount = (marginBalance?.()?.additional / price) + Math.abs(marginBalance?.()?.unwindBuyQty)
    const marginSelected = form.getFieldValue('marginSelected'); // 是否使用杠杆
    const maxBuyAvail = marginBalance?.()?.openMargin && marginSelected ? marginAvblBuyAmount : spotAvblBuyAmount;
    // if (marginAvblBuyAmount < spotAvblBuyAmount && !['USDT_USD', 'USDT_HKD'].includes(coinDetail?.coinCode)) {
    //   maxBuyAvail = marginAvblBuyAmount
    // }
    updateSlider({ maxAvail: maxBuyAvail, value, price })
    checkTotalIsValid()
  }

  // 校验购买量
  const checkBaseQtyValid = () => {
    if (!isLogin) {
      return
    }
    const baseQty = form.getFieldValue('baseQty');
    let errorMsgAmount = '';
    if (typeof baseQty === 'undefined' || !baseQty) {
      errorMsgAmount = I18n.t('Mpg9o5vL')
      updateValidState({ errorMsgAmount, amountStatus: 'error' })
      return false
    }
    updateValidState({ errorMsgAmount, amountStatus: 'success' })
    return true
  }

  // 校验成交总额
  const checkTotalIsValid = () => {
    // 没有登录 无需校验
    if (!isLogin) {
      return
    }

    let errorMsgTotal = ''

    const orderTotal = form.getFieldValue('orderTotal')
    // 如果没有输入成交总额 或者成交总额是空字符串 提示用户需要输入成交额
    if (typeof orderTotal === 'undefined' || !orderTotal) {
      errorMsgTotal = I18n.t('q3DOozIm')
      updateValidState({ errorMsgTotal, totalStatus: 'error' })
      return false
    }

    const orderQuoteMin = Number(coinDetail?.orderQuoteMin) / (1 + Number(coinDetail?.marketFreezeBuffer ?? 0))
    // 如果用户输入的成交额 小于等于0 提示用户 成交额不能小于 xxx
    if (Number(orderTotal) <= 0) {
      errorMsgTotal = I18n.t('minTotalMessage', {
        amount: helper.formatFixNumString(orderQuoteMin, coinDetail?.tradeVolumePrecision)
      })

      updateValidState({ errorMsgTotal, totalStatus: 'error' })

      return false
    }

    // 如果设置的最小成交额 大于 0 但是用户输入的成交额小于设置的最小成交额 提示用户 成交额不能小于 xxx
    if (Number(coinDetail?.orderQuoteMin) > 0 && Number(orderTotal) < Number(coinDetail?.orderQuoteMin)) {
      errorMsgTotal = I18n.t('minTotalMessage', {
        amount: helper.formatFixNumString(orderQuoteMin, coinDetail?.tradeVolumePrecision)
      })

      updateValidState({ errorMsgTotal, totalStatus: 'error' })

      return false
    }

    const orderQuoteMax = Number(coinDetail?.orderQuoteMax) / (1 + Number(coinDetail?.marketFreezeBuffer ?? 0));
    // 设置的最大成交额大于0 并且 用户输入的成交额已经大于最大成交额 提示用户 单笔交易不得大于 xxx
    if (orderQuoteMax > 0 && Number(orderTotal) > orderQuoteMax) {
      errorMsgTotal = I18n.t('oneMaxTotalMessage', {
        amount: helper.formatFixNumString(orderQuoteMax, coinDetail?.tradePrecision)
      })

      updateValidState({ errorMsgTotal, totalStatus: 'error' })

      return false
    }

    const price = coinDetail?.ask;
    const marginSelected = form.getFieldValue('marginSelected'); // 是否使用杠杆

    // 最大可用 包含杠杆
    const maxAvailTotal = marginBalance?.()?.additional + Math.abs(marginBalance?.()?.unwindBuyQty) * price
    // 现货可用
    const spotAvblTotal = marginBalance?.()?.quoteMarketAvailable ?? 0
    // 如果开了杠杆 并且使用杠杆 当前的可用就是最大可用 否则就是现货
    const avail = marginBalance?.()?.openMargin && marginSelected ? maxAvailTotal : spotAvblTotal;
    // if (maxAvailTotal < spotAvblTotal && !['USDT_USD', 'USDT_HKD'].includes(coinDetail?.coinCode)) {
    //   avail = maxAvailTotal
    // }

    const requestTotal = Number(orderTotal);
    // 如果用户输入的成交额大于可用额 提示用户可用额最大为xxx
    if (Number(requestTotal ?? 0) > Number(avail ?? 0)) {
      errorMsgTotal = marginSelected ? I18n.t('CmkHjovb') : I18n.t('ZdTV8l92');

      updateValidState({ errorMsgTotal: (<MaxBuyAvblMessage errorMsgTotal={errorMsgTotal} />), totalStatus: 'error' })

      return false
    }
    // 如果用户输入的成交额 不小于最小成交额 不大于最大成交额 不大于最大可用 则是一个合理的成交额 校验通过
    updateValidState({ errorMsgTotal, totalStatus: 'success' })
    return true
  }

  // 用户停用启用杠杆
  const onMarginSelected = async (checked) => {
    if (!isLogin) {
      jumpLogin()
      return
    }
    const spotAvbl = maxAvblAndSpotAvblRef.current.spotAvbl;
    const maxAvbl = maxAvblAndSpotAvblRef.current.maxAvbl;
    publicMarginSelected({ checked, isSpotAvblGreaterThanMaxAvbl: Number(spotAvbl) > Number(maxAvbl), onSlider })
  }
  // 杠杆的禁用状态 现货余额大于最大可用 并且弹框弹出过
  const getMarginDisabled = () => handleMarginDisabled({
    spotAvbl: maxAvblAndSpotAvblRef.current.spotAvbl, maxAvbl: maxAvblAndSpotAvblRef.current.maxAvbl
  })

  // 下单
  const onFinish = async (values) => {
    if (!submitValidata()) {
      return
    }

    createTradeOrder({
      order_type: 'market',
      side: 'buy',
      price: ''
    })
  }

  const submitValidata = () => {
    return [checkBaseQtyValid(), checkTotalIsValid()].every(res => res)
  }

  return {
    form,
    onBaseQtyChange,
    onOrderTotalChange,
    onFocus: setCurrentInputFocus,
    onSlider,
    renderMaxAvbl,
    renderSpotAvbl,
    renderEstimateBorrow,
    getMarginDisabled,
    onMarginSelected,
    loading,
    onFinish,
    validState
  }
}
